import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Megaphone,
  Send,
  Info,
  AlertTriangle,
  AlertCircle,
  Users,
  Search,
  X,
  Loader2,
  ChevronDown,
  ChevronRight,
  Link2,
  Mail,
  Eye,
  Layers,
} from 'lucide-react';
import { AdminLayout } from '../../components/Admin/AdminLayout';
import { useAuthStore } from '../../store/authStore';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

type Severity = 'info' | 'warn' | 'critical';
type SegmentType = 'all' | 'plan' | 'specific_users';
type PlanTier = 'free' | 'pro' | 'agency' | 'enterprise';

interface AdminUserMini {
  id: string;
  email: string;
  name: string;
  subscription: string;
}

interface BroadcastRow {
  id: string;
  title: string;
  body: string;
  severity: Severity;
  link: string | null;
  segmentType: SegmentType;
  segmentValue: Record<string, unknown> | null;
  recipientCount: number;
  sentAt: string;
  adminId: string | null;
  adminEmail: string | null;
}

interface RecipientRow {
  userId: string;
  userEmail: string | null;
  readAt: string | null;
  deliveredAt: string;
}

interface NotificationTemplateMeta {
  templateKey: string;
  type: string;
  category: string;
  severity: Severity;
}

const SEVERITY_META: Record<Severity, { icon: React.ComponentType<{ className?: string }>; text: string; badge: string; card: string }> = {
  info: {
    icon: Info,
    text: 'text-blue-500',
    badge: 'bg-blue-100 text-blue-700 border-blue-200',
    card: 'bg-blue-50/50',
  },
  warn: {
    icon: AlertTriangle,
    text: 'text-amber-500',
    badge: 'bg-amber-100 text-amber-700 border-amber-200',
    card: 'bg-amber-50/50',
  },
  critical: {
    icon: AlertCircle,
    text: 'text-red-500',
    badge: 'bg-red-100 text-red-700 border-red-200',
    card: 'bg-red-50/50',
  },
};

export const AdminBroadcasts: React.FC = () => {
  const { t, i18n } = useTranslation('admin');
  const [searchParams] = useSearchParams();
  const token = useAuthStore((s) => s.token);

  const [allUsers, setAllUsers] = useState<AdminUserMini[]>([]);
  const [history, setHistory] = useState<BroadcastRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState<Severity>('info');
  const [link, setLink] = useState('');
  const [segmentType, setSegmentType] = useState<SegmentType>('all');
  const [segmentPlan, setSegmentPlan] = useState<PlanTier>('pro');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);

  // Phase 10: template registry. Loaded once on mount; the dropdown
  // pre-fills title/body using the EN i18n entry (admin always
  // composes in EN — recipients see their preferred locale because
  // each row stores the template_key + vars).
  const [templates, setTemplates] = useState<NotificationTemplateMeta[]>([]);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>('');

  const authHeader = useMemo(
    (): HeadersInit => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  const loadUsers = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/admin/users`, { headers: authHeader });
      if (!res.ok) return;
      const json = (await res.json()) as AdminUserMini[];
      setAllUsers(json);
    } catch (err) {
      console.error('[broadcasts] loadUsers failed', err);
    }
  }, [token, authHeader]);

  const loadHistory = useCallback(async () => {
    if (!token) return;
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/broadcasts`, { headers: authHeader });
      if (!res.ok) throw new Error(String(res.status));
      const json = await res.json();
      setHistory(Array.isArray(json.broadcasts) ? json.broadcasts : []);
    } catch (err) {
      console.error('[broadcasts] loadHistory failed', err);
      toast.error(t('broadcasts.errors.loadHistoryFailed'));
    } finally {
      setHistoryLoading(false);
    }
  }, [token, authHeader, t]);

  const loadTemplates = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/admin/notification-templates`, {
        headers: authHeader,
      });
      if (!res.ok) return;
      const json = await res.json();
      const list = Array.isArray(json.templates) ? json.templates : [];
      setTemplates(list);
    } catch (err) {
      console.error('[broadcasts] loadTemplates failed', err);
    }
  }, [token, authHeader]);

  useEffect(() => {
    loadUsers();
    loadHistory();
    loadTemplates();
  }, [loadUsers, loadHistory, loadTemplates]);

  /**
   * When the admin picks a template from the dropdown, pre-fill the
   * title/body fields using i18n so they can edit before sending.
   * The default i18n entries use `{{var}}` placeholders — we leave
   * those literal so the admin sees what they're filling in (e.g.
   * `Your {{plan}} plan expires in 7 days`). For broadcasts we don't
   * have a single user's vars, so the admin should swap the
   * placeholders with concrete copy before sending.
   *
   * The chosen template severity also flows into the form so the
   * preview chip matches what the registry says it should be.
   */
  const handleApplyTemplate = (key: string) => {
    setSelectedTemplateKey(key);
    if (!key) return;
    const meta = templates.find((tt) => tt.templateKey === key);
    if (!meta) return;
    const i18nTitle = i18n.t(`notifications.templates.${key}.title`, {
      ns: 'common',
      defaultValue: '',
    });
    const i18nBody = i18n.t(`notifications.templates.${key}.body`, {
      ns: 'common',
      defaultValue: '',
    });
    if (i18nTitle) setTitle(String(i18nTitle));
    if (i18nBody) setBody(String(i18nBody));
    setSeverity(meta.severity);
  };

  const previewEnTitle = useMemo(() => {
    if (!selectedTemplateKey) return '';
    return String(
      i18n.getFixedT('en', 'common')(
        `notifications.templates.${selectedTemplateKey}.title`,
        { defaultValue: '' },
      ),
    );
  }, [selectedTemplateKey, i18n]);
  const previewEnBody = useMemo(() => {
    if (!selectedTemplateKey) return '';
    return String(
      i18n.getFixedT('en', 'common')(
        `notifications.templates.${selectedTemplateKey}.body`,
        { defaultValue: '' },
      ),
    );
  }, [selectedTemplateKey, i18n]);
  const previewThTitle = useMemo(() => {
    if (!selectedTemplateKey) return '';
    return String(
      i18n.getFixedT('th', 'common')(
        `notifications.templates.${selectedTemplateKey}.title`,
        { defaultValue: '' },
      ),
    );
  }, [selectedTemplateKey, i18n]);
  const previewThBody = useMemo(() => {
    if (!selectedTemplateKey) return '';
    return String(
      i18n.getFixedT('th', 'common')(
        `notifications.templates.${selectedTemplateKey}.body`,
        { defaultValue: '' },
      ),
    );
  }, [selectedTemplateKey, i18n]);

  // Pre-select a user when redirected from the usage panel's row menu.
  useEffect(() => {
    const userId = searchParams.get('userId');
    if (userId && allUsers.some((u) => u.id === userId)) {
      setSegmentType('specific_users');
      setSelectedUserIds((prev) => (prev.includes(userId) ? prev : [...prev, userId]));
    }
  }, [searchParams, allUsers]);

  const userSearchResults = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return [] as AdminUserMini[];
    return allUsers
      .filter((u) => {
        const hay = `${u.email} ${u.name}`.toLowerCase();
        return hay.includes(q) && !selectedUserIds.includes(u.id);
      })
      .slice(0, 20);
  }, [userSearch, allUsers, selectedUserIds]);

  const selectedUsers = useMemo(
    () => allUsers.filter((u) => selectedUserIds.includes(u.id)),
    [allUsers, selectedUserIds],
  );

  const recipientEstimate = useMemo(() => {
    if (segmentType === 'all') {
      return allUsers.filter((u) => (u as AdminUserMini & { role?: string }).id).length;
    }
    if (segmentType === 'plan') {
      return allUsers.filter((u) => {
        const normalised = u.subscription === 'plus' ? 'pro' : u.subscription;
        return normalised === segmentPlan;
      }).length;
    }
    return selectedUserIds.length;
  }, [segmentType, segmentPlan, selectedUserIds, allUsers]);

  const canSubmit = title.trim().length > 0 && body.trim().length > 0 && recipientEstimate > 0;

  const handleSend = async () => {
    if (!token) return;
    setSending(true);
    try {
      const segment: Record<string, unknown> = { type: segmentType };
      if (segmentType === 'plan') segment.plan = segmentPlan;
      if (segmentType === 'specific_users') segment.userIds = selectedUserIds;

      const res = await fetch(`${API_URL}/admin/broadcasts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          title: title.trim(),
          body: body.trim(),
          severity,
          link: link.trim() || null,
          segment,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || String(res.status));
      }
      const json = await res.json();
      const delivered = Number(json.recipientCount) || 0;
      const attempted = Number(json.attemptedCount) || delivered;
      const failed = Number(json.failedCount) || Math.max(0, attempted - delivered);
      // Distinguish three outcomes — success / partial / total-failure —
      // because "ส่งแล้ว 0 คน" silently looking like a success was the bug
      // that motivated the dedup-constraint fix in the first place.
      if (delivered === 0 && attempted > 0) {
        toast.error(t('broadcasts.toasts.allFailed', { attempted }), { duration: 8000 });
      } else if (failed > 0) {
        toast.error(t('broadcasts.toasts.partial', { delivered, attempted, failed }), {
          duration: 8000,
        });
      } else {
        toast.success(t('broadcasts.toasts.sent', { count: delivered }));
      }
      setTitle('');
      setBody('');
      setLink('');
      setSeverity('info');
      setSelectedUserIds([]);
      setSegmentType('all');
      setShowConfirm(false);
      await loadHistory();
    } catch (err) {
      console.error('[broadcasts] send failed', err);
      toast.error(t('broadcasts.errors.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Megaphone className="w-8 h-8 text-purple-600" />
            {t('broadcasts.title')}
          </h1>
          <p className="text-gray-600 mt-1">{t('broadcasts.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Compose */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Send className="w-5 h-5 text-purple-500" />
              {t('broadcasts.compose.title')}
            </h2>

            {/* Phase 10 — template picker. Pre-fills title/body and
                bumps severity to whatever the template registry says.
                Admin can edit before sending. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <span className="inline-flex items-center gap-1">
                  <Layers className="w-4 h-4 text-gray-500" />
                  {t('broadcasts.compose.templatePicker')}
                </span>
              </label>
              <select
                value={selectedTemplateKey}
                onChange={(e) => handleApplyTemplate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">
                  {t('broadcasts.compose.templatePickerNone')}
                </option>
                {templates.map((tpl) => (
                  <option key={tpl.templateKey} value={tpl.templateKey}>
                    {tpl.templateKey} · {tpl.category} · {tpl.severity}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {t('broadcasts.compose.templatePickerHint')}
              </p>
              {selectedTemplateKey && (previewEnTitle || previewThTitle) && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <p className="text-[11px] font-semibold text-gray-500 mb-1">
                      {t('broadcasts.compose.templatePreviewEn')}
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {previewEnTitle || '—'}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 whitespace-pre-line">
                      {previewEnBody || '—'}
                    </p>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <p className="text-[11px] font-semibold text-gray-500 mb-1">
                      {t('broadcasts.compose.templatePreviewTh')}
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {previewThTitle || '—'}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 whitespace-pre-line">
                      {previewThBody || '—'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('broadcasts.compose.titleLabel')}
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                placeholder={t('broadcasts.compose.titlePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('broadcasts.compose.bodyLabel')}
              </label>
              <textarea
                rows={4}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={t('broadcasts.compose.bodyPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('broadcasts.compose.severity')}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {(['info', 'warn', 'critical'] as Severity[]).map((s) => {
                  const meta = SEVERITY_META[s];
                  const Icon = meta.icon;
                  const active = severity === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSeverity(s)}
                      className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        active
                          ? `${meta.badge} ring-2 ring-offset-1 ring-blue-400`
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className={`w-4 h-4 ${meta.text}`} />
                      {t(`broadcasts.severity.${s}`)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <span className="inline-flex items-center gap-1">
                  <Link2 className="w-4 h-4 text-gray-500" />
                  {t('broadcasts.compose.link')}
                </span>
              </label>
              <input
                type="text"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="/dashboard"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('broadcasts.compose.segment')}
              </label>
              <div className="space-y-2">
                {(['all', 'plan', 'specific_users'] as SegmentType[]).map((s) => (
                  <label
                    key={s}
                    className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer text-sm ${
                      segmentType === s
                        ? 'border-blue-400 bg-blue-50 text-blue-900'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="segmentType"
                      checked={segmentType === s}
                      onChange={() => setSegmentType(s)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span>{t(`broadcasts.segment.${s}`)}</span>
                  </label>
                ))}
              </div>

              {segmentType === 'plan' && (
                <select
                  value={segmentPlan}
                  onChange={(e) => setSegmentPlan(e.target.value as PlanTier)}
                  className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="free">{t('broadcasts.plans.free')}</option>
                  <option value="pro">{t('broadcasts.plans.pro')}</option>
                  <option value="agency">{t('broadcasts.plans.agency')}</option>
                  <option value="enterprise">{t('broadcasts.plans.enterprise')}</option>
                </select>
              )}

              {segmentType === 'specific_users' && (
                <div className="mt-3 space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder={t('broadcasts.compose.searchUsers')}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {userSearchResults.length > 0 && (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-40 overflow-y-auto">
                      {userSearchResults.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                            setSelectedUserIds((prev) => [...prev, u.id]);
                            setUserSearch('');
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Mail className="w-3 h-3 text-gray-400" />
                          <span className="font-medium text-gray-900">{u.name}</span>
                          <span className="text-gray-500">· {u.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedUsers.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">
                        {t('broadcasts.compose.selected', { count: selectedUsers.length })}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {selectedUsers.map((u) => (
                          <span
                            key={u.id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-100 text-purple-700 text-xs"
                          >
                            {u.email}
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedUserIds((prev) => prev.filter((id) => id !== u.id))
                              }
                              className="hover:text-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <span className="text-xs text-gray-600 inline-flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                {t('broadcasts.compose.recipientsEstimate', { count: recipientEstimate })}
              </span>
              <button
                type="button"
                disabled={!canSubmit || sending}
                onClick={() => setShowConfirm(true)}
                className="px-5 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white rounded-lg text-sm font-medium flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                {t('broadcasts.compose.send')}
              </button>
            </div>
          </div>

          {/* Preview + History */}
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <Eye className="w-5 h-5 text-blue-500" />
                {t('broadcasts.preview.title')}
              </h2>
              <BroadcastPreview title={title} body={body} severity={severity} />
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('broadcasts.history.title')}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t('broadcasts.history.subtitle')}
                </p>
              </div>
              <div className="max-h-[640px] overflow-y-auto divide-y divide-gray-100">
                {historyLoading ? (
                  <div className="px-6 py-12 text-center text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                    {t('broadcasts.history.loading')}
                  </div>
                ) : history.length === 0 ? (
                  <div className="px-6 py-12 text-center text-gray-500 text-sm">
                    {t('broadcasts.history.empty')}
                  </div>
                ) : (
                  history.map((b) => <BroadcastHistoryRow key={b.id} broadcast={b} />)
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showConfirm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => !sending && setShowConfirm(false)}
        >
          <div
            className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              {t('broadcasts.confirm.title')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('broadcasts.confirm.body', { count: recipientEstimate })}
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
              <BroadcastPreview title={title} body={body} severity={severity} />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={sending}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {t('broadcasts.confirm.cancel')}
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white rounded-lg text-sm font-medium flex items-center gap-2"
              >
                {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('broadcasts.confirm.send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

interface PreviewProps {
  title: string;
  body: string;
  severity: Severity;
}

const BroadcastPreview: React.FC<PreviewProps> = ({ title, body, severity }) => {
  const meta = SEVERITY_META[severity];
  const Icon = meta.icon;
  const { t } = useTranslation('admin');
  return (
    <div
      className={`border rounded-lg px-3 py-3 flex gap-3 ${meta.card} border-gray-200`}
    >
      <Icon className={`w-5 h-5 flex-shrink-0 ${meta.text}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900">
          {title.trim() || t('broadcasts.preview.placeholderTitle')}
        </p>
        <p className="text-xs text-gray-600 mt-1 whitespace-pre-line">
          {body.trim() || t('broadcasts.preview.placeholderBody')}
        </p>
        <p className="text-[11px] text-gray-400 mt-1.5">{t('broadcasts.preview.justNow')}</p>
      </div>
    </div>
  );
};

interface HistoryRowProps {
  broadcast: BroadcastRow;
}

const BroadcastHistoryRow: React.FC<HistoryRowProps> = ({ broadcast }) => {
  const { t } = useTranslation('admin');
  const token = useAuthStore((s) => s.token);
  const [expanded, setExpanded] = useState(false);
  const [recipients, setRecipients] = useState<RecipientRow[] | null>(null);
  const [readCount, setReadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const authHeader = useMemo<Record<string, string>>(() => {
    const h: Record<string, string> = {};
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, [token]);
  const loadedRef = useRef(false);

  const meta = SEVERITY_META[broadcast.severity];
  const Icon = meta.icon;

  const toggleExpand = useCallback(async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !loadedRef.current) {
      loadedRef.current = true;
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/admin/broadcasts/${broadcast.id}/recipients`, {
          headers: authHeader,
        });
        if (!res.ok) throw new Error(String(res.status));
        const json = await res.json();
        setRecipients(Array.isArray(json.recipients) ? json.recipients : []);
        setReadCount(Number(json.totalRead || 0));
      } catch (err) {
        console.error('[broadcasts] loadRecipients failed', err);
        toast.error(t('broadcasts.errors.loadRecipientsFailed'));
      } finally {
        setLoading(false);
      }
    }
  }, [expanded, broadcast.id, authHeader, t]);

  const segmentLabel =
    broadcast.segmentType === 'all'
      ? t('broadcasts.segment.all')
      : broadcast.segmentType === 'plan'
        ? t('broadcasts.history.segmentPlan', {
            plan: String((broadcast.segmentValue?.plan ?? '') || '—'),
          })
        : t('broadcasts.history.segmentSpecific', {
            count: Array.isArray(broadcast.segmentValue?.userIds)
              ? (broadcast.segmentValue?.userIds as string[]).length
              : 0,
          });

  return (
    <div className="px-6 py-3">
      <button
        type="button"
        onClick={toggleExpand}
        className="w-full flex items-start gap-3 text-left"
      >
        <Icon className={`w-4 h-4 mt-1 flex-shrink-0 ${meta.text}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{broadcast.title}</p>
          <p className="text-xs text-gray-500 mt-0.5 flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${meta.badge}`}>
              {broadcast.severity}
            </span>
            <span>{segmentLabel}</span>
            <span>·</span>
            <span>{t('broadcasts.history.delivered', { count: broadcast.recipientCount })}</span>
            <span>·</span>
            <span>{new Date(broadcast.sentAt).toLocaleString()}</span>
            {broadcast.adminEmail && (
              <>
                <span>·</span>
                <span className="text-gray-400">{broadcast.adminEmail}</span>
              </>
            )}
          </p>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="mt-3 ml-7 border-l-2 border-gray-100 pl-4">
          <p className="text-xs text-gray-600 whitespace-pre-line mb-3">{broadcast.body}</p>
          {loading ? (
            <p className="text-xs text-gray-500">
              <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
              {t('broadcasts.history.loadingRecipients')}
            </p>
          ) : recipients ? (
            <div className="space-y-1">
              <p className="text-xs text-gray-500">
                {t('broadcasts.history.readRate', {
                  read: readCount,
                  total: recipients.length,
                  pct:
                    recipients.length > 0
                      ? Math.round((readCount / recipients.length) * 100)
                      : 0,
                })}
              </p>
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded mt-2">
                <table className="min-w-full text-xs">
                  <tbody>
                    {recipients.slice(0, 100).map((r) => (
                      <tr key={r.userId} className="border-t border-gray-100">
                        <td className="px-2 py-1 text-gray-700 truncate">{r.userEmail || r.userId}</td>
                        <td className="px-2 py-1 text-gray-500">
                          {r.readAt ? (
                            <span className="text-green-600">
                              {t('broadcasts.history.read', { time: new Date(r.readAt).toLocaleString() })}
                            </span>
                          ) : (
                            <span className="text-gray-400">
                              {t('broadcasts.history.unread')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default AdminBroadcasts;
